<script>
  import Carousel from '@leaflink/stash/Carousel.vue';
  import Loading from '@leaflink/stash/Loading.vue';
  import { storeToRefs } from 'pinia';
  import { defineAsyncComponent, inject } from 'vue';

  import { ContentSponsoredRetrievePlacementEnum } from '@/clients/unifiedApi';
  import { useSponsorship } from '@/composables/useSponsorship';
  import ToggleStoreFavorite from '@/pages/Shop/Purchasing/components/ToggleStoreFavorite';
  import { useShopStore } from '@/piniaStores/shop/shop.store';
  import { useUserStore } from '@/piniaStores/user/user.store';
  import { i18n } from '@/plugins';

  import BatchInformation from './BatchInformation.vue';
  import ProductDescription from './ProductDescription.vue';
  import ProductInformation from './ProductInformation.vue';

  export default {
    name: 'ProductDetail',

    components: {
      ProductDescription,
      ProductInformation,
      ProductRecommendation: defineAsyncComponent(() =>
        import('@/components/ProductRecommendation/ProductRecommendation.vue'),
      ),
      BatchInformation,
      ToggleStoreFavorite,
      Carousel,
      Loading,
    },

    props: {
      brandId: {
        type: [Number, String],
        required: true,
      },

      brandSlug: {
        type: String,
        default: '',
      },

      companySlug: {
        type: String,
        required: false,
        default: '',
      },

      isPreview: Boolean,

      isPublicMenu: Boolean,

      productId: {
        type: [Number, String],
        required: true,
      },

      /**
       * For the "source" property of the tracking event.
       * Ex: "PDP Modal"
       */
      trackingSource: {
        type: String,
        required: true,
      },
    },

    emits: ['close', 'setProduct', 'refresh-product'],

    data() {
      return {
        isFavorited: false,
        isUpdatingFavoriteStatus: false,
        product: undefined,
      };
    },

    computed: {
      /**
       * Returns the product images, defaulting to featured image if not available
       */
      displayedProductImages() {
        if (!this.product.images.length) {
          return [{ imageUrl: this.product.featuredImage }];
        }

        return this.product.images.map((src) => ({ imageUrl: src }));
      },
    },

    setup() {
      const shopStore = useShopStore();

      const { fetchActiveCampaign, fetchPreviewProduct, fetchProductDetail, fetchPublicMenuProductDetail } = shopStore;

      const { getIsSplitEnabled, userActiveCompany } = storeToRefs(useUserStore());

      const { refreshRecommendedProducts, recommendedProducts } = useSponsorship(userActiveCompany?.value?.id, {
        isBuyer: true,
      });

      const productDetailErrorHandler = inject('productDetailErrorHandler', null);

      const productRecommendationCarouselTitle = i18n.t('productRecommendation.carousel.title.similarProducts');

      return {
        recommendedProducts,
        fetchActiveCampaign,
        fetchPreviewProduct,
        fetchProductDetail,
        fetchPublicMenuProductDetail,
        getIsSplitEnabled,
        refreshRecommendedProducts,
        productDetailErrorHandler,
        productRecommendationCarouselTitle,
      };
    },

    watch: {
      productId: {
        handler() {
          this.product = undefined;
          this.fetchProduct();
          this.$refs.scrollTarget && this.$refs.scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
        },
        immediate: true,
      },
    },

    methods: {
      handleClose() {
        this.$emit('close');
      },

      async handleFetchProductDetail({ productId, brandId, companySlug }) {
        try {
          return await this.fetchProductDetail({ productId, brandId, companySlug });
        } catch (e) {
          if (this.productDetailErrorHandler) {
            this.productDetailErrorHandler(e);
          } else {
            throw e;
          }
        }
      },

      /**
       * Fetches product detail info
       */
      async fetchProduct() {
        const { brandId, brandSlug, productId, companySlug } = this;

        if (this.isPublicMenu) {
          this.product = await this.fetchPublicMenuProductDetail({ brand: brandSlug, productId });

          return;
        }

        await Promise.all([
          this.fetchActiveCampaign(),
          (this.product = this.isPreview
            ? await this.fetchPreviewProduct({ productId, brandId })
            : await this.handleFetchProductDetail({ productId, brandId, companySlug })),
        ]);

        this.isFavorited = this.product?.isOnPurchaseList;

        if (!this.isPreview && !this.isPublicMenu && this.product) {
          await this.refreshRecommendedProducts({
            objectId: [this.productId],
            placement: ContentSponsoredRetrievePlacementEnum.RecommendedProductsPdmAlternative,
            category: [this.product.category.id],
            subcategory: [this.product.subCategory.id],
          });
        }

        this.$emit('setProduct', this.product);
      },

      onRefreshProduct(product) {
        this.$emit('refresh-product', product);
      },
    },
  };
</script>

<template>
  <div>
    <div ref="scrollTarget"></div>
    <Loading v-if="!product" />
    <div v-else class="ll-grid">
      <div class="relative md:col-span-6" :class="$style.images">
        <Carousel :slides="displayedProductImages">
          <template #slide="{ slide }">
            <img class="tw-w-full md:tw-aspect-square md:tw-object-contain" :src="slide.imageUrl" :alt="product.name" />
          </template>
        </Carousel>
        <ToggleStoreFavorite
          v-if="product && !isPublicMenu"
          :is-favorited="isFavorited"
          :product="product"
          :tracking-source="trackingSource"
          variant="over-image"
          :variant-options="{ position: 'top-right' }"
          @success="() => (isFavorited = !isFavorited)"
        />
      </div>

      <!-- Product information -->
      <div class="md:col-span-6" :class="$style.information">
        <p v-if="!isPublicMenu" @click="handleClose">
          <router-link class="color-ice-700" :to="{ name: 'brand-menu', params: { brandId: product.brand.id } }">
            {{ product.brand.company.name }}
          </router-link>
        </p>
        <h2 class="ll-mb-2" data-test="detail|product-name">{{ product.name }}</h2>

        <!-- pricing information -->
        <slot name="pricing"></slot>

        <ProductInformation :product="product" :is-public-menu="isPublicMenu" />

        <!-- product description -->
        <div v-if="recommendedProducts.length" class="tw-mt-6">
          <ProductDescription data-test="product-description-sidebar" :product="product" />
        </div>
        <div v-if="product.batchInformation.length">
          <h3 class="ll-mt-3 ll-mb-2">{{ $t('brand_menu.batchesResults') }}</h3>
          <BatchInformation
            v-for="(batch, index) in product.batchInformation"
            :key="batch.id"
            :is-open="index === 0"
            :batch="batch"
          />
        </div>
      </div>

      <div class="md:col-span-6">
        <ProductDescription
          v-if="!recommendedProducts.length"
          data-test="product-description-footer"
          :product="product"
        />

        <ProductRecommendation
          v-if="!isPreview && recommendedProducts.length"
          class="max-lg:tw-p-3 tw-pt-6 tw-border-t tw-border-ice-200"
          placement-name="recommended-products-pdm-alternative"
          :items="recommendedProducts"
          :items-sm="3"
          :items-md="3"
          :items-lg="3"
          :items-xl="3"
          :open-product-detail-modal="false"
          :placement-title="productRecommendationCarouselTitle"
          @refresh-product="onRefreshProduct"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
  @include breakpoint('md') {
    .images {
      grid-row: 1/1;
    }

    .information {
      grid-row: 1/4;
    }
  }
</style>
